import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useUserAuth } from '../context/UserAuthContext';
import {
  Box,
  TextField,
  Button,
  Typography,
  Container,
  Paper,
  Alert,
  CircularProgress,
  InputAdornment
} from '@mui/material';
import EmailIcon from '@mui/icons-material/Email';
import LockIcon from '@mui/icons-material/Lock';
import NavBar from './shared/NavBar';
import Footer from './shared/Footer';
import WhatsAppSupport from './shared/WhatsAppSupport';
import { trackUserLoggedIn } from '../utils/googleAnalytics';

const UserLogin = () => {
  const navigate = useNavigate();
  const { requestOTP, verifyOTP, isAuthenticated, loading: sessionLoading } = useUserAuth();
  
  const [step, setStep] = useState(1); // 1: Email, 2: OTP
  const [identifier, setIdentifier] = useState('');
  const [identifierType, setIdentifierType] = useState(null); // 'email'
  const [otp, setOtp] = useState('');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [loading, setLoading] = useState(false);
  const [resendTimer, setResendTimer] = useState(0);
  const [otpExpiry, setOtpExpiry] = useState(null);
  const [devOtp, setDevOtp] = useState(null);

  // Redirect if already authenticated (only after session validation finishes)
  useEffect(() => {
    if (!sessionLoading && isAuthenticated) {
      navigate('/user-dashboard');
    }
  }, [isAuthenticated, sessionLoading, navigate]);

  // Resend timer countdown
  useEffect(() => {
    if (resendTimer > 0) {
      const timer = setTimeout(() => setResendTimer(resendTimer - 1), 1000);
      return () => clearTimeout(timer);
    }
  }, [resendTimer]);

  // OTP expiry countdown
  useEffect(() => {
    if (otpExpiry) {
      const interval = setInterval(() => {
        const now = new Date();
        const expiry = new Date(otpExpiry);
        const diff = Math.floor((expiry - now) / 1000);
        
        if (diff <= 0) {
          setError('OTP has expired. Please request a new one.');
          setStep(1);
          setOtp('');
          setOtpExpiry(null);
        }
      }, 1000);
      
      return () => clearInterval(interval);
    }
  }, [otpExpiry]);

  // Detect identifier type as user types
  const detectIdentifierType = (value) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    
    if (emailRegex.test(value)) {
      return 'email';
    }
    return null;
  };

  const handleRequestOTP = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');

    // Detect identifier type
    const type = detectIdentifierType(identifier);
    
    if (!type) {
      setError('Please enter a valid email address');
      return;
    }

    const normalizedIdentifier = type === 'email' ? identifier.trim().toLowerCase() : identifier;
    setIdentifier(normalizedIdentifier);

    setLoading(true);

    const result = await requestOTP(normalizedIdentifier);

    setLoading(false);

    if (result.success) {
      setIdentifierType(result.data.type || type);
      setSuccess('OTP sent successfully! Please check your email.');
      setStep(2);
      setResendTimer(60); // 60 seconds before allowing resend
      if (result.data.expiresAt) {
        setOtpExpiry(result.data.expiresAt);
      }
      // Store OTP for development display
      if (result.data.otp) {
        setDevOtp(result.data.otp);
      }
      // Auto-focus OTP input
      setTimeout(() => {
        document.getElementById('otp-input')?.focus();
      }, 100);
    } else {
      setError(result.message || 'Failed to send OTP. Please try again.');
    }
  };

  const handleVerifyOTP = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');

    // Validate OTP
    if (!/^\d{4}$/.test(otp)) {
      setError('Please enter a valid 4-digit OTP');
      return;
    }

    const normalizedIdentifier = identifierType === 'email' || detectIdentifierType(identifier) === 'email'
      ? identifier.trim().toLowerCase()
      : identifier;

    setLoading(true);

    const result = await verifyOTP(normalizedIdentifier, otp);

    setLoading(false);

    if (result.success) {
      trackUserLoggedIn(normalizedIdentifier);
      setSuccess('Login successful! Redirecting...');
      setTimeout(() => {
        navigate('/user-dashboard');
      }, 1000);
    } else {
      setError(result.message || 'Invalid OTP. Please try again.');
    }
  };

  const handleResendOTP = async () => {
    setOtp('');
    setError('');
    setSuccess('');
    setLoading(true);

    const normalizedIdentifier = identifierType === 'email' || detectIdentifierType(identifier) === 'email'
      ? identifier.trim().toLowerCase()
      : identifier;

    const result = await requestOTP(normalizedIdentifier);

    setLoading(false);

    if (result.success) {
      setSuccess('OTP resent successfully!');
      setResendTimer(60);
      if (result.data.expiresAt) {
        setOtpExpiry(result.data.expiresAt);
      }
      if (result.data.otp) {
        setDevOtp(result.data.otp);
      }
    } else {
      setError(result.message || 'Failed to resend OTP.');
    }
  };

  const handleBackToIdentifier = () => {
    setStep(1);
    setOtp('');
    setError('');
    setSuccess('');
    setOtpExpiry(null);
    setIdentifierType(null);
  };

  const getRemainingTime = () => {
    if (!otpExpiry) return null;
    const now = new Date();
    const expiry = new Date(otpExpiry);
    const diff = Math.floor((expiry - now) / 1000);
    if (diff <= 0) return null;
    const minutes = Math.floor(diff / 60);
    const seconds = diff % 60;
    return `${minutes}:${seconds.toString().padStart(2, '0')}`;
  };

  return (
    <Box sx={{ minHeight: '100vh', display: 'flex', flexDirection: 'column' }}>
      <NavBar />
      <Box
        sx={{
          flex: 1,
          background: 'linear-gradient(135deg, #0a0a0a 0%, #1a1a1a 30%, #2a2a2a 70%, #1a1a1a 100%)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          py: 4
        }}
      >
      <Container component="main" maxWidth="xs">
        <Paper
          elevation={10}
          sx={{
            p: 4,
            background: 'rgba(255, 255, 255, 0.05)',
            backdropFilter: 'blur(10px)',
            border: '1px solid rgba(255, 255, 255, 0.1)',
            borderRadius: 3
          }}
        >
          <Box sx={{ textAlign: 'center', mb: 3 }}>
            <Typography variant="h4" component="h1" gutterBottom sx={{ fontWeight: 600, color: '#FF6B35' }}>
              Welcome
            </Typography>
            <Typography variant="body1" color="text.secondary" sx={{ mb: 3, color: 'rgba(255, 255, 255, 0.7)' }}>
              Enter your registered email to receive an OTP
            </Typography>
          </Box>

          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}

          {success && (
            <Alert severity="success" sx={{ mb: 2 }}>
              {success}
            </Alert>
          )}

          {step === 1 ? (
            <Box component="form" onSubmit={handleRequestOTP}>
              <TextField
                fullWidth
                label="Email Address"
                value={identifier}
                onChange={(e) => setIdentifier(e.target.value)}
                placeholder="Enter your email address"
                variant="outlined"
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <EmailIcon color="primary" />
                    </InputAdornment>
                  ),
                  sx: {
                    color: 'white',
                    '& .MuiOutlinedInput-notchedOutline': {
                      borderColor: 'rgba(255, 255, 255, 0.3)'
                    },
                    '&:hover .MuiOutlinedInput-notchedOutline': {
                      borderColor: '#FF6B35'
                    },
                    '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                      borderColor: '#FF6B35'
                    }
                  }
                }}
                InputLabelProps={{
                  sx: { color: 'rgba(255, 255, 255, 0.7)' }
                }}
                sx={{ mb: 3 }}
              />
              <Button
                type="submit"
                fullWidth
                variant="contained"
                disabled={loading || !identifier}
                sx={{
                  mt: 3,
                  mb: 2,
                  py: 1.5,
                  background: 'linear-gradient(45deg, #FF6B35 30%, #FFB74D 70%)',
                  '&:hover': {
                    background: 'linear-gradient(45deg, #FF8C35 30%, #FFC74D 70%)',
                    transform: 'translateY(-2px)',
                    boxShadow: '0 6px 20px rgba(255, 107, 53, 0.4)'
                  },
                  transition: 'all 0.3s ease'
                }}
              >
                {loading ? <CircularProgress size={24} sx={{ color: 'white' }} /> : 'Send OTP'}
              </Button>
            </Box>
          ) : (
            <Box component="form" onSubmit={handleVerifyOTP}>
              <TextField
                margin="normal"
                required
                fullWidth
                id="otp-input"
                label="Enter OTP"
                name="otp"
                autoFocus
                value={otp}
                onChange={(e) => setOtp(e.target.value.replace(/\D/g, '').slice(0, 4))}
                placeholder="4-digit OTP"
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <LockIcon sx={{ color: '#FF6B35' }} />
                    </InputAdornment>
                  ),
                  sx: {
                    color: 'white',
                    fontSize: '1.5rem',
                    letterSpacing: '0.5rem',
                    textAlign: 'center',
                    '& .MuiOutlinedInput-notchedOutline': {
                      borderColor: 'rgba(255, 255, 255, 0.3)'
                    },
                    '&:hover .MuiOutlinedInput-notchedOutline': {
                      borderColor: '#FF6B35'
                    },
                    '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                      borderColor: '#FF6B35'
                    }
                  }
                }}
                InputLabelProps={{
                  sx: { color: 'rgba(255, 255, 255, 0.7)' }
                }}
              />

              {getRemainingTime() && (
                <Typography
                  variant="caption"
                  sx={{
                    display: 'block',
                    textAlign: 'center',
                    color: '#06AED5',
                    mt: 1
                  }}
                >
                  OTP expires in: {getRemainingTime()}
                </Typography>
              )}

              {devOtp && (
                <Box
                  sx={{
                    mt: 2,
                    p: 2,
                    background: 'linear-gradient(135deg, rgba(255, 107, 53, 0.2) 0%, rgba(6, 174, 213, 0.2) 100%)',
                    border: '2px dashed #FF6B35',
                    borderRadius: 2,
                    textAlign: 'center'
                  }}
                >
                  <Typography
                    variant="caption"
                    sx={{
                      display: 'block',
                      color: 'rgba(255, 255, 255, 0.7)',
                      mb: 0.5
                    }}
                  >
                    🔧 Development Mode
                  </Typography>
                  <Typography
                    variant="h5"
                    sx={{
                      color: '#FF6B35',
                      fontWeight: 700,
                      letterSpacing: '0.5rem'
                    }}
                  >
                    {devOtp}
                  </Typography>
                  <Typography
                    variant="caption"
                    sx={{
                      display: 'block',
                      color: 'rgba(255, 255, 255, 0.5)',
                      mt: 0.5
                    }}
                  >
                    Your OTP for testing
                  </Typography>
                </Box>
              )}

              <Button
                type="submit"
                fullWidth
                variant="contained"
                disabled={loading || otp.length !== 4}
                sx={{
                  mt: 3,
                  mb: 2,
                  py: 1.5,
                  background: 'linear-gradient(45deg, #FF6B35 30%, #FFB74D 70%)',
                  '&:hover': {
                    background: 'linear-gradient(45deg, #FF8C35 30%, #FFC74D 70%)',
                    transform: 'translateY(-2px)',
                    boxShadow: '0 6px 20px rgba(255, 107, 53, 0.4)'
                  },
                  transition: 'all 0.3s ease'
                }}
              >
                {loading ? <CircularProgress size={24} sx={{ color: 'white' }} /> : 'Verify & Login'}
              </Button>

              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Button
                  onClick={handleBackToIdentifier}
                  sx={{
                    color: 'rgba(255, 255, 255, 0.7)',
                    textTransform: 'none',
                    '&:hover': {
                      color: '#FF6B35'
                    }
                  }}
                >
                  Change Email
                </Button>
                <Button
                  onClick={handleResendOTP}
                  disabled={resendTimer > 0 || loading}
                  sx={{
                    color: resendTimer > 0 ? 'rgba(255, 255, 255, 0.3)' : '#06AED5',
                    textTransform: 'none',
                    '&:hover': {
                      color: '#4FC3F7'
                    }
                  }}
                >
                  {resendTimer > 0 ? `Resend in ${resendTimer}s` : 'Resend OTP'}
                </Button>
              </Box>
            </Box>
          )}

          <WhatsAppSupport
            title="Unable to login?"
            description="If you are not receiving OTP or cannot sign in, message us on WhatsApp and we will help you quickly."
            message={`I am having issue in Login: ${identifier || '<phone> <email>'}`}
            sx={{ mt: 3 }}
          />
        </Paper>
      </Container>
      </Box>
      <Footer />
    </Box>
  );
};

export default UserLogin;
